import React, { useState, useEffect } from 'react';
import { webhookAPI } from '../services/api';
import '../styles/PendingCustomersPage.css';

function PendingCustomersPage() {
  const [pendingCustomers, setPendingCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [successMessage, setSuccessMessage] = useState('');

  // Load pending customers on mount
  useEffect(() => {
    loadPendingCustomers();
  }, []);

  const loadPendingCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await webhookAPI.getPendingCustomers();
      setPendingCustomers(response.data.data || []);
    } catch (err) {
      console.error('Error loading pending customers:', err);
      setError('Failed to load pending customers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedCustomer) return;

    try {
      await webhookAPI.approvePendingCustomer(selectedCustomer.id, 'admin');
      setSuccessMessage(`✅ ${selectedCustomer.name} אושר בהצלחה`);
      setShowApproveModal(false);
      setSelectedCustomer(null);
      loadPendingCustomers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to approve customer: ' + err.message);
    }
  };

  const handleReject = async () => {
    if (!selectedCustomer) return;

    try {
      await webhookAPI.rejectPendingCustomer(selectedCustomer.id, rejectReason, 'admin');
      setSuccessMessage(`❌ ${selectedCustomer.name} נדחה בהצלחה`);
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedCustomer(null);
      loadPendingCustomers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to reject customer: ' + err.message);
    }
  };

  const getFilteredCustomers = () => {
    if (filter === 'all') return pendingCustomers;
    return pendingCustomers.filter(c => c.status === filter);
  };

  const filteredCustomers = getFilteredCustomers();

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge badge-pending">⏳ בהמתנה</span>;
      case 'approved':
        return <span className="badge badge-approved">✅ אושר</span>;
      case 'rejected':
        return <span className="badge badge-rejected">❌ נדחה</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('he-IL');
  };

  if (loading) {
    return <div className="pending-customers-page"><div className="loading">טוען...</div></div>;
  }

  return (
    <div className="pending-customers-page">
      <h1>📋 ניהול לקוחות ממתינים</h1>

      {error && <div className="error-banner">{error}</div>}
      {successMessage && <div className="success-banner">{successMessage}</div>}

      <div className="filter-section">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            ⏳ בהמתנה ({pendingCustomers.filter(c => c.status === 'pending').length})
          </button>
          <button
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            ✅ אושרו ({pendingCustomers.filter(c => c.status === 'approved').length})
          </button>
          <button
            className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            ❌ נדחו ({pendingCustomers.filter(c => c.status === 'rejected').length})
          </button>
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            הכל ({pendingCustomers.length})
          </button>
        </div>
      </div>

      {filteredCustomers.length === 0 ? (
        <div className="no-customers">
          <p>אין לקוחות ממתינים</p>
        </div>
      ) : (
        <div className="customers-table-container">
          <table className="customers-table">
            <thead>
              <tr>
                <th>שם</th>
                <th>מספר זהות</th>
                <th>אימייל</th>
                <th>טלפון</th>
                <th>קטגוריה</th>
                <th>ספרים</th>
                <th>סכום</th>
                <th>תאריך</th>
                <th>סטטוס</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr key={customer.id} className={`customer-row status-${customer.status}`}>
                  <td className="name">{customer.name}</td>
                  <td>{customer.zeout}</td>
                  <td>{customer.email || '-'}</td>
                  <td>{customer.phone || '-'}</td>
                  <td>{customer.category_id || '-'}</td>
                  <td className="center">{customer.books_in_subscription}</td>
                  <td className="center">₪{customer.amount}</td>
                  <td className="center">{formatDate(customer.created_at)}</td>
                  <td className="center">{getStatusBadge(customer.status)}</td>
                  <td className="actions">
                    {customer.status === 'pending' && (
                      <>
                        <button
                          className="btn-approve"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setShowApproveModal(true);
                          }}
                          title="אשר לקוח"
                        >
                          ✅ אישור
                        </button>
                        <button
                          className="btn-reject"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setShowRejectModal(true);
                          }}
                          title="דחה לקוח"
                        >
                          ❌ דחייה
                        </button>
                      </>
                    )}
                    {customer.status === 'approved' && (
                      <span className="status-label">אושר</span>
                    )}
                    {customer.status === 'rejected' && (
                      <div className="rejection-info">
                        <span className="status-label">נדחה</span>
                        {customer.rejection_reason && (
                          <div className="rejection-reason" title={customer.rejection_reason}>
                            {customer.rejection_reason}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedCustomer && (
        <div className="modal-overlay">
          <div className="modal approve-modal">
            <h2>✅ אישור לקוח</h2>
            <div className="customer-info">
              <p><strong>שם:</strong> {selectedCustomer.name}</p>
              <p><strong>מספר זהות:</strong> {selectedCustomer.zeout}</p>
              <p><strong>אימייל:</strong> {selectedCustomer.email || '-'}</p>
              <p><strong>טלפון:</strong> {selectedCustomer.phone || '-'}</p>
              <p><strong>כתובת:</strong> {selectedCustomer.address || '-'}</p>
              <p><strong>קטגוריה:</strong> {selectedCustomer.category_id}</p>
              <p><strong>מספר ספרים:</strong> {selectedCustomer.books_in_subscription}</p>
              <p><strong>סכום:</strong> ₪{selectedCustomer.amount}</p>
            </div>
            <p className="confirmation-text">
              האם אתה בטוח שברצונך לאשר לקוח זה? הוא יתווסף לטבלת הלקוחות.
            </p>
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleApprove}>
                ✅ אישור
              </button>
              <button className="btn-secondary" onClick={() => {
                setShowApproveModal(false);
                setSelectedCustomer(null);
              }}>
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedCustomer && (
        <div className="modal-overlay">
          <div className="modal reject-modal">
            <h2>❌ דחיית לקוח</h2>
            <div className="customer-info">
              <p><strong>שם:</strong> {selectedCustomer.name}</p>
              <p><strong>מספר זהות:</strong> {selectedCustomer.zeout}</p>
            </div>
            <label htmlFor="rejection-reason">סיבת הדחייה:</label>
            <textarea
              id="rejection-reason"
              className="rejection-textarea"
              placeholder="הקלד את סיבת הדחייה (רשות)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows="4"
            />
            <p className="confirmation-text">
              האם אתה בטוח שברצונך לדחות לקוח זה?
            </p>
            <div className="modal-actions">
              <button className="btn-danger" onClick={handleReject}>
                ❌ דחייה
              </button>
              <button className="btn-secondary" onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
                setSelectedCustomer(null);
              }}>
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PendingCustomersPage;
